/**
 * Parses a list of values from text, where each value is separated by a
 * newline, comma, or tab. Values are trimmed and deduplicated.
 *
 * @param {string} text - The pasted text.
 * @return {string[]} The distinct values found, in the order they appear.
 */
export function parsePastedList(text: string): string[] {
  const seen = new Set<string>();
  const values: string[] = [];

  for (const raw of text.split(/[\n\r,\t]+/)) {
    const value = raw.trim();
    if (!value || seen.has(value)) continue;
    seen.add(value);
    values.push(value);
  }

  return values;
}

/** Represents one selectable option in a filter. */
export type PasteListOption = {
  id: string;
  name: string;
};

export type PasteListMatch = {
  /** Filter option IDs that were matched in the pasted blob. */
  matched: string[];
  /** Pasted values that have no matching filter option. */
  unmatched: string[];
};

/**
 * Sorts a list of values into options that exist in the filter and those that
 * don't. Matching is case-insensitive.
 *
 * A pasted value is matched to either an option's ID or name, with priority
 * given to option IDs in case an option has a name that matches another
 * option's ID.
 *
 * @param {string[]} values - Values from `parsePastedList`.
 * @param {PasteListOption[]} options - The filter's options.
 * @return {PasteListMatch} The matched option IDs and the unmatched values.
 */
export function matchPastedList(
  values: string[],
  options: PasteListOption[]
): PasteListMatch {
  const byId = new Map(options.map((o) => [o.id.toLowerCase(), o.id]));
  const byName = new Map(options.map((o) => [o.name.toLowerCase(), o.id]));
  const matched: string[] = [];
  const unmatched: string[] = [];
  const matchedIds = new Set<string>();

  for (const value of values) {
    const key = value.toLowerCase();
    const id = byId.get(key) ?? byName.get(key);

    if (id === undefined) {
      unmatched.push(value);
      continue;
    }

    // Two pasted values can point to the same option (ex: "TX" and "Texas").
    if (matchedIds.has(id)) continue;

    matchedIds.add(id);
    matched.push(id);
  }

  return { matched, unmatched };
}
